import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { FaxDocPicker } from "./FaxDocPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/fax`;
const MARKS = ["Urgent", "For review", "Please comment", "Please reply", "Please recycle"];
const FIELDS = [["to_name", "To — name or office"], ["to_fax", "To — fax"], ["to_phone", "To — phone"], ["from_name", "From — name"], ["from_fax", "From — fax"], ["from_phone", "From — phone"], ["from_email", "From — email"], ["regarding", "Regarding"], ["pages", "Pages (incl. cover)"]];

// Branded cover sheet for faxes you send out: fill it in here or download a blank to write on.
export const OutgoingFaxCover = ({ canCompany, canSend = true }) => {
  const [open, setOpen] = useState(false);
  const [sender, setSender] = useState("household");
  const [f, setF] = useState({ notes: "", marks: [] });
  const [busy, setBusy] = useState("");
  const load = async (s) => {
    setSender(s);
    try { const d = (await axios.get(`${API}/out-cover/defaults`, { params: { sender: s } })).data; setF((x) => ({ ...x, ...d })); } catch { /* defaults are optional */ }
  };
  const start = () => { setF({ notes: "", marks: [] }); setOpen(true); load("household"); };
  const make = async (blank) => {
    setBusy(blank ? "blank" : "filled");
    try {
      const r = await axios.post(`${API}/out-cover.pdf`, { ...f, sender, blank }, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = u; a.download = `hearthvault-fax-cover${sender === "company" ? "-company" : ""}${blank ? "-blank" : ""}.pdf`; a.click(); setTimeout(() => URL.revokeObjectURL(u), 5000);
      toast.success(blank ? "Blank cover sheet downloaded" : "Cover sheet ready to send");
    } catch { toast.error("Couldn't make the cover sheet"); } finally { setBusy(""); }
  };
  const mark = (m) => setF((x) => ({ ...x, marks: x.marks.includes(m) ? x.marks.filter((y) => y !== m) : [...x.marks, m] }));
  return (
    <>
      <button type="button" onClick={start} data-testid="fax-out-cover-btn" className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground"><FileText className="h-3.5 w-3.5" /> Cover sheet for faxes you send</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fax-out-cover-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Fax cover sheet</DialogTitle>
            <DialogDescription>Branded with HearthVault. Fill it in and download a ready-to-send PDF, or grab a blank one to write on.</DialogDescription>
          </DialogHeader>
          {canCompany && (
            <div className="inline-flex rounded-full border border-border p-0.5 text-xs w-fit" data-testid="fax-out-sender">
              {[["household", "From the family"], ["company", "From HearthVault (company)"]].map(([id, label]) => (
                <button key={id} type="button" onClick={() => load(id)} data-testid={`fax-out-sender-${id}`} className={`px-3 py-1 rounded-full transition-colors ${sender === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{label}</button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {FIELDS.map(([k, label]) => (
              <Input key={k} placeholder={label} aria-label={label} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={k === "regarding" || k === "to_name" ? "col-span-2" : ""} data-testid={`fax-out-${k}`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="fax-out-marks">
            {MARKS.map((m) => (
              <button key={m} type="button" onClick={() => mark(m)} data-testid={`fax-out-mark-${m.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${f.marks.includes(m) ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary"}`}>{m}</button>
            ))}
          </div>
          <Textarea rows={4} placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="fax-out-notes" />
          {canSend && <FaxDocPicker cover={f} sender={sender} />}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={!!busy} onClick={() => make(true)} data-testid="fax-out-blank">{busy === "blank" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Blank to print"}</Button>
            <Button size="sm" className="rounded-full gap-1" disabled={!!busy} onClick={() => make(false)} data-testid="fax-out-download">{busy === "filled" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download filled PDF</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
